// Shared presentational helpers for the CFRI demo portal. Cards and metric
// tiles come from the funder portal (FunderUI) so the two portals read alike.
import React, { useEffect, useState } from "react";
import { Sigma, Sparkles, ExternalLink, Maximize2, X } from "lucide-react";
import NetworkCanvas from "../NetworkCanvas";
import { mapUrl } from "../../service/CfriService";

export const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-US") : n);

// The report marks every statement as "computed" (counted from the literature,
// reproducible) or "engine" (drafted by the assessment engine). That
// distinction is the point of the report, so it is always labeled in words.
const PROVENANCE = {
    computed: { icon: Sigma, label: "Computed", title: "Counted from the published literature; the same inputs give the same result" },
    engine: { icon: Sparkles, label: "Engine", title: "Drafted by the 42Degrees assessment engine from the papers it cites" },
};

export function ProvenanceTag({ kind }) {
    const p = PROVENANCE[kind] || PROVENANCE.computed;
    const Icon = p.icon;
    return (
        <span className={`cfri-prov cfri-prov-${kind === "engine" ? "engine" : "computed"}`} title={p.title}>
            <Icon size={11} />
            {p.label}
        </span>
    );
}

// The briefing's section sources read "computed: the people whose ..." or
// "quoted from the proposal"; tag the kind and keep the rest as a note.
export function SourceLine({ source }) {
    if (!source) return null;
    const m = source.match(/^(computed|engine)(?::\s*(.*))?$/);
    if (m) {
        return (
            <span className="cfri-source">
                <ProvenanceTag kind={m[1]} />
                {m[2] ? <span>{m[2]}</span> : null}
            </span>
        );
    }
    return <span className="cfri-source"><span>{source.charAt(0).toUpperCase() + source.slice(1)}</span></span>;
}

export function PaperLink({ paper, showYear = true }) {
    if (!paper) return null;
    return (
        <>
            <a className="cfri-paper-link" href={paper.url} target="_blank" rel="noopener noreferrer">
                {paper.title}
                <ExternalLink size={11} className="cfri-paper-link-icon" />
            </a>
            {showYear && paper.year ? <span className="cfri-muted"> ({paper.year})</span> : null}
        </>
    );
}

function MapViewport({ src, title }) {
    const [loading, setLoading] = useState(true);
    return (
        <div className="cfri-map">
            {loading && (
                <div className="cfri-map-loading">
                    <NetworkCanvas />
                    <p>Loading the map…</p>
                </div>
            )}
            <iframe
                title={title}
                src={src}
                loading="lazy"
                // The map compiles its own viewer in the browser; give it a moment after load
                onLoad={() => setTimeout(() => setLoading(false), 1500)}
                style={{ visibility: loading ? "hidden" : "visible" }}
            />
        </div>
    );
}

/**
 * One of the report's interactive maps, served by the backend into an iframe.
 * On phones it opens over the page instead of sitting in it: embedded, it
 * would fill most of the screen and take the swipes meant to scroll the page.
 */
export function MapFrame({ id, title }) {
    const src = mapUrl(id);
    const [smallScreen, setSmallScreen] = useState(window.innerWidth < 768);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onResize = () => setSmallScreen(window.innerWidth < 768);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    if (!smallScreen) {
        return (
            <>
                <MapViewport src={src} title={title} />
                <div className="cfri-map-actions">
                    <a className="funder-btn-ghost" href={src} target="_blank" rel="noopener noreferrer">
                        <Maximize2 size={13} /> Open full screen
                    </a>
                </div>
            </>
        );
    }

    return (
        <>
            <button type="button" className="cfri-map-poster" onClick={() => setOpen(true)}>
                <NetworkCanvas />
                <span className="funder-btn"><Maximize2 size={14} /> Open the interactive map</span>
            </button>
            {open && (
                <div className="cfri-map-overlay" role="dialog" aria-modal="true" aria-label={title}>
                    <div className="cfri-map-overlay-bar">
                        <span>{title}</span>
                        <button type="button" className="funder-more-close" onClick={() => setOpen(false)} aria-label="Close the map"><X size={20} /></button>
                    </div>
                    <MapViewport src={src} title={title} />
                </div>
            )}
        </>
    );
}
